import { useCallback, useState } from "react";
import {
  getTournamentBracket,
  getTournamentCategoryMatchSettings,
  getTournamentSchedule,
  listTournamentRegistrations,
  type AdminTournamentBracket,
  type AdminTournamentCategoryMatchSettings,
  type AdminTournamentSchedule,
} from "../services/eventRegistrationAdminService";
import {
  createScheduleDraft,
  createSettingsDraft,
  manualGroupId,
  pairLabelFromRegistration,
} from "../utils/tournamentOperations";

type ManualGroupAssignments = Record<string, string>;

export type AdminTournamentManualPair = {
  registrationId: number;
  label: string;
};

type UseAdminTournamentResourcesParams = {
  categoryKey: string;
  manualGroupCount: string;
  onError?: (message: string) => void;
  token: string;
  tournamentId: number;
};

export function useAdminTournamentResources({
  categoryKey,
  manualGroupCount,
  onError,
  token,
  tournamentId,
}: UseAdminTournamentResourcesParams) {
  const [settings, setSettings] =
    useState<AdminTournamentCategoryMatchSettings | null>(null);
  const [settingsDraft, setSettingsDraft] = useState(createSettingsDraft());
  const [bracket, setBracket] = useState<AdminTournamentBracket | null>(null);
  const [schedule, setSchedule] = useState<AdminTournamentSchedule | null>(null);
  const [scheduleDraft, setScheduleDraft] = useState(createScheduleDraft());
  const [manualPairs, setManualPairs] = useState<AdminTournamentManualPair[]>(
    [],
  );
  const [manualAssignments, setManualAssignments] =
    useState<ManualGroupAssignments>({});
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [loadingBracket, setLoadingBracket] = useState(false);
  const [loadingSchedule, setLoadingSchedule] = useState(false);

  const loadSettings = useCallback(async () => {
    if (!token || !tournamentId || !categoryKey) return;
    setLoadingSettings(true);
    try {
      const next = await getTournamentCategoryMatchSettings(
        token,
        tournamentId,
        categoryKey,
      );
      setSettings(next);
      setSettingsDraft(createSettingsDraft(next));
    } catch (error) {
      onError?.(
        error instanceof Error
          ? error.message
          : "Não foi possível carregar configurações.",
      );
    } finally {
      setLoadingSettings(false);
    }
  }, [categoryKey, onError, token, tournamentId]);

  const loadBracket = useCallback(async () => {
    if (!token || !tournamentId || !categoryKey) {
      setBracket(null);
      return;
    }
    setLoadingBracket(true);
    try {
      setBracket(await getTournamentBracket(token, tournamentId, categoryKey));
    } catch (_err) {
      setBracket(null);
    } finally {
      setLoadingBracket(false);
    }
  }, [categoryKey, token, tournamentId]);

  const loadSchedule = useCallback(async () => {
    if (!token || !tournamentId) {
      setSchedule(null);
      return;
    }
    setLoadingSchedule(true);
    try {
      const next = await getTournamentSchedule(token, tournamentId);
      setSchedule(next);
      setScheduleDraft(createScheduleDraft(next.config));
    } catch (_err) {
      setSchedule(null);
    } finally {
      setLoadingSchedule(false);
    }
  }, [token, tournamentId]);

  const loadManualPairs = useCallback(async () => {
    if (!token || !tournamentId || !categoryKey) {
      setManualPairs([]);
      return;
    }
    try {
      const response = await listTournamentRegistrations(token, tournamentId, {
        status: "confirmed",
        registrationCategoryKey: categoryKey,
      });
      const pairs = (response.registrations || [])
        .filter((registration) => {
          const registrationCategory = String(
            registration.registrationCategoryKey || "",
          ).trim();
          const paymentStatus = String(
            registration.paymentStatus || "",
          ).toLowerCase();
          return (
            registrationCategory === categoryKey &&
            String(registration.registrationType || "").toLowerCase() ===
              "fixed_pair" &&
            Boolean(registration.partnerName) &&
            (paymentStatus === "confirmed" ||
              paymentStatus === "paid" ||
              paymentStatus === "not_required")
          );
        })
        .map((registration) => ({
          registrationId: Number(registration.id),
          label: pairLabelFromRegistration(registration),
        }));
      setManualPairs(pairs);
      setManualAssignments((current) => {
        const next: ManualGroupAssignments = {};
        pairs.forEach((pair, index) => {
          next[String(pair.registrationId)] =
            current[String(pair.registrationId)] ||
            manualGroupId(index % Math.max(1, Number(manualGroupCount || 2)));
        });
        return next;
      });
    } catch (_error) {
      setManualPairs([]);
    }
  }, [categoryKey, manualGroupCount, token, tournamentId]);

  return {
    bracket,
    loadingBracket,
    loadingSchedule,
    loadingSettings,
    loadBracket,
    loadManualPairs,
    loadSchedule,
    loadSettings,
    manualAssignments,
    manualPairs,
    schedule,
    scheduleDraft,
    setBracket,
    setManualAssignments,
    setSchedule,
    setScheduleDraft,
    setSettings,
    setSettingsDraft,
    settings,
    settingsDraft,
  };
}
